import JSONViewer from '@/components/json-viewer';
import LinearLoading from '@/components/linear-loading';
import { useUserToken } from '@/contexts/UserTokenContext';
import { endpoints } from '@/models/ops/endpoints/EndpointTool.model';
import { EndpointToolService } from '@/services/ops/endpoints/EndpointTool.service';
import { LoadingButton } from '@mui/lab';
import {
  Card,
  CardContent,
  FormControl,
  Grid,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';

import { Field, Form, Formik } from 'formik';
import { FC, useState } from 'react';
import * as yup from 'yup';

const getPlaceholders = (url: string): string[] => {
  const regex = /\{(\w+)}/g;
  const matches = url.match(regex);
  return matches ? matches.map(match => match.slice(1, -1)) : [];
};

const parsePlaceholderValues = (
  valuesString: string
): Record<string, string> => {
  if (!valuesString || !valuesString.trim()) return {};
  return valuesString.split(',').reduce(
    (acc, pair) => {
      const [key, value] = pair.split('=').map(s => s.trim());
      if (key && value) {
        acc[key] = value;
      }
      return acc;
    },
    {} as Record<string, string>
  );
};

const getFinalUrl = (url: string, placeholderValues: string): string => {
  const placeholders = getPlaceholders(url);
  if (placeholders.length === 0) return url;

  const placeholderValuesMap = parsePlaceholderValues(placeholderValues);
  let finalUrl = url;
  placeholders.forEach(placeholder => {
    const regex = new RegExp(`\\{${placeholder}\\}`, 'g');
    finalUrl = finalUrl.replace(
      regex,
      placeholderValuesMap[placeholder] || `{${placeholder}}`
    );
  });
  return finalUrl;
};

const validationSchema = yup.object().shape({
  endpointName: yup.string().required('Endpoint is required'),
  payload: yup.string().test({
    test: function (value) {
      if (value) {
        try {
          JSON.parse(value);
          return true;
        } catch (e) {
          return this.createError({
            message: 'Payload must be valid JSON',
            path: this.path
          });
        }
      }
      return true;
    }
  }),
  placeholderValues: yup.string().test({
    test: function (value) {
      const selectedEndpoint = endpoints.find(
        e => e.name === this.parent.endpointName
      );
      const placeholders = selectedEndpoint
        ? getPlaceholders(selectedEndpoint.url)
        : [];

      if (placeholders.length === 0) return true;

      const providedValues = parsePlaceholderValues(value);
      const missingPlaceholders = placeholders.filter(p => !providedValues[p]);

      if (missingPlaceholders.length > 0) {
        return this.createError({
          message: `Missing values: ${missingPlaceholders.join(', ')}. Use format: name1=value1,name2=value2`,
          path: this.path
        });
      }

      const pairs = value.split(',');
      for (const pair of pairs) {
        const parts = pair.split('=');
        if (parts.length !== 2 || !parts[0].trim() || !parts[1].trim()) {
          return this.createError({
            message: `Invalid format near "${pair}". Use format: name1=value1,name2=value2`,
            path: this.path
          });
        }
      }
      return true;
    }
  })
});

export const EndpointToolRoute: FC = () => {
  const [result, setResult] = useState(null);
  const [selectedEndpoint, setSelectedEndpoint] = useState(undefined);
  const { token, userHasValidToken } = useUserToken();

  const allowedEndpoints = endpoints.filter(
    e =>
      userHasValidToken &&
      e.permissions.some(p => token?.permissions?.includes(p))
  );

  const endpointMutation = useMutation(
    ['EndpointToolService.makeRequest'],
    async (values: {
      endpointName: string;
      payload: string;
      placeholderValues: string;
    }) => {
      const endpoint = endpoints.find(e => e.name === values.endpointName);
      let finalUrl = endpoint.url;
      const placeholders = getPlaceholders(endpoint.url);

      if (placeholders.length > 0) {
        const placeholderValuesMap = parsePlaceholderValues(
          values.placeholderValues
        );
        placeholders.forEach(placeholder => {
          const regex = new RegExp(`\\{${placeholder}\\}`, 'g');
          finalUrl = finalUrl.replace(regex, placeholderValuesMap[placeholder]);
        });
      }

      return EndpointToolService.makeRequest({
        endpoint: { ...endpoint, url: finalUrl },
        payload: values.payload
      });
    },
    {
      onError: (error: any) => {
        setResult({ error: error?.message || 'An error occurred' });
      },
      onSuccess: response => {
        setResult(response);
      }
    }
  );

  const handleSubmit = async (values: {
    endpointName: string;
    payload: string;
    placeholderValues: string;
  }) => {
    setResult(null);
    endpointMutation.mutate(values);
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography gutterBottom variant='h4'>
          Generic access to Backend endpoints for Production Support
        </Typography>
      </Grid>
      <Grid item xs={4}>
        <Card>
          <CardContent>
            <Formik
              initialValues={{
                endpointName: '',
                payload: '',
                placeholderValues: ''
              }}
              onSubmit={handleSubmit}
              validationSchema={validationSchema}>
              {formik => {
                const currentEndpoint = allowedEndpoints.find(
                  e => e.name === formik.values.endpointName
                );

                if (currentEndpoint !== selectedEndpoint) {
                  setSelectedEndpoint(currentEndpoint);
                  if (currentEndpoint) {
                    formik.setFieldValue(
                      'payload',
                      currentEndpoint.bodyTemplate || ''
                    );
                    formik.setFieldValue(
                      'placeholderValues',
                      getPlaceholders(currentEndpoint.url)
                        .map(s => s + '=')
                        .join(', ')
                    );
                  }
                }

                const urlPlaceholders = selectedEndpoint
                  ? getPlaceholders(selectedEndpoint.url)
                  : [];

                return (
                  <Form>
                    <Stack spacing={2}>
                      <FormControl fullWidth>
                        <Field
                          as={Select}
                          error={
                            formik.touched.endpointName &&
                            Boolean(formik.errors.endpointName)
                          }
                          name='endpointName'
                          onChange={(
                            e: React.ChangeEvent<{ value: unknown }>
                          ) => {
                            formik.handleChange(e);
                            const newEndpoint = allowedEndpoints.find(
                              endpoint => endpoint.name === e.target.value
                            );
                            if (newEndpoint) {
                              setSelectedEndpoint(newEndpoint);
                              formik.setFieldValue(
                                'payload',
                                newEndpoint.bodyTemplate || ''
                              );
                              formik.setFieldValue(
                                'placeholderValues',
                                getPlaceholders(newEndpoint.url)
                                  .map(s => s + '=')
                                  .join(', ')
                              );
                            }
                          }}
                          placeholder='Plese select'>
                          {allowedEndpoints.map(endpoint => (
                            <MenuItem key={endpoint.name} value={endpoint.name}>
                              {endpoint.name} ({endpoint.method})
                            </MenuItem>
                          ))}
                        </Field>
                      </FormControl>

                      <TextField
                        InputProps={{
                          readOnly: true
                        }}
                        fullWidth
                        label='URL'
                        value={getFinalUrl(
                          selectedEndpoint?.url || '',
                          formik.values.placeholderValues
                        )}
                      />

                      {urlPlaceholders.length > 0 && (
                        <TextField
                          error={
                            formik.touched.placeholderValues &&
                            Boolean(formik.errors.placeholderValues)
                          }
                          fullWidth
                          helperText={
                            (formik.touched.placeholderValues &&
                              formik.errors.placeholderValues) ||
                            'Comma-separated key=value pairs'
                          }
                          label='URL Parameters'
                          name='placeholderValues'
                          onBlur={formik.handleBlur}
                          onChange={formik.handleChange}
                          value={formik.values.placeholderValues}
                        />
                      )}

                      {selectedEndpoint?.method !== 'GET' && (
                        <TextField
                          error={
                            formik.touched.payload &&
                            Boolean(formik.errors.payload)
                          }
                          fullWidth
                          helperText={
                            formik.touched.payload && formik.errors.payload
                          }
                          label='Payload (JSON)'
                          multiline
                          name='payload'
                          onBlur={formik.handleBlur}
                          onChange={formik.handleChange}
                          rows={4}
                          value={formik.values.payload}
                        />
                      )}

                      <LoadingButton
                        color='primary'
                        loading={endpointMutation.isLoading}
                        type='submit'
                        variant='contained'>
                        Submit
                      </LoadingButton>
                    </Stack>
                  </Form>
                );
              }}
            </Formik>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={8}>
        <Card>
          <CardContent>
            <Typography gutterBottom variant='h6'>
              Response
            </Typography>
            {endpointMutation.isLoading ? (
              <LinearLoading />
            ) : (
              result && (
                <JSONViewer collapseStringsAfterLength={200} json={result} />
              )
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

EndpointToolRoute.displayName = 'EndpointToolRoute';
